import {
  FinanceRate,
  InfoWorkType,
  type WorkType,
} from '@kanda-libs/ks-frontend-services';
import { pipe } from 'fp-ts/lib/function';
import { capitalise } from './String-functions';

export const CHANGE_TYPE: Partial<Record<WorkType, WorkType>> = {
  roofing_excl_flat_roofs: 'roofing',
  door_internal: 'doors',
};

export const REPLACE_WORK_TYPE_NAME: Partial<Record<WorkType, string>> = {
  bedrooms_furnitue_custom: 'Bedrooms Furniture (Custom)',
  blinds_awnings_shutters: 'Blinds, Awnings and/or Shutters',
  cctv: 'CCTV',
  ev_charger: 'EV Charger',
  flooring_excluding_carpets: 'Flooring (Exlcuding Carpets)',
  insulation_roof_foil_wrap_only: 'Insulation (Roof Foil Wrap Only)',
  kitchens_fitted: 'Kitchens (Fitted)',
  kitchens_supply_only: 'Kitchens (Supply Only)',
  roofing_excl_flat_roofs: 'Roofing (Exclusing Flat Roofs)',
};

export const changeRenamedWorkTypes = (workTypes: WorkType[]): WorkType[] =>
  workTypes
    .map((type) =>
      Object.keys(CHANGE_TYPE).includes(type)
        ? CHANGE_TYPE[type] || undefined
        : type,
    )
    .filter((type) => type !== undefined);

export const filterDeprecated = (workTypes: WorkType[]): WorkType[] =>
  workTypes.filter((type) => !['other', 'carpentry'].includes(type));

export const stripDeprecatedWorkTypes = (workTypes: WorkType[]): WorkType[] =>
  pipe(workTypes, filterDeprecated);

export interface WorkTypeOption {
  name: string;
  value: string;
}

export const mapWorkTypesToOptions = (
  workTypes: WorkType[],
): WorkTypeOption[] =>
  workTypes.map((type) => {
    if (Object.keys(REPLACE_WORK_TYPE_NAME).includes(type))
      return {
        name: REPLACE_WORK_TYPE_NAME[type] as string,
        value: type,
      };
    return {
      name: type.split('_').map(capitalise).join(' '),
      value: type,
    };
  });

export const formatWorkTypesToOptions = (
  workTypes: WorkType[],
): WorkTypeOption[] =>
  pipe(
    workTypes,
    stripDeprecatedWorkTypes,
    changeRenamedWorkTypes,
    mapWorkTypesToOptions,
  );

export const rateValuesKey = (rate: FinanceRate): string => {
  const fee = String(rate.fee);
  const minDep = String(rate.min_deposit_value || 0);
  const minDepPct = String(rate.min_deposit_pct || 0);
  const maxDep = String(rate.max_deposit_value || 0);
  const maxDepPct = String(rate.max_deposit_pct || 0);
  const minVal = String(rate.min_job_value || 0);
  const maxVal = String(rate.max_job_value || 0);
  const bnpl = rate?.apr_type === 'BUYNOW_PAYLATER';
  return [
    fee,
    minDep,
    minDepPct,
    maxDep,
    maxDepPct,
    minVal,
    maxVal,
    bnpl ? 'bnpl' : undefined,
  ]
    .filter(Boolean)
    .join('-');
};

export const getNameFromRate = (rate: FinanceRate): string => {
  const { provider, name } = rate;
  return `${provider}-${name}-${rateValuesKey(rate)}`;
};

export const getUniqueFinanceRatePositions = (
  rates: FinanceRate[],
): boolean[] =>
  rates
    .map((rate) => getNameFromRate(rate))
    .map(
      (name: string, index: number, names: string[]) =>
        names.indexOf(name) === index,
    );

export const extractUniqueFinanceRates = (
  rates: FinanceRate[],
): FinanceRate[] =>
  pipe(rates, getUniqueFinanceRatePositions, (positions) =>
    rates.filter((_, index) => positions[index]),
  );

export const reduceInfoWorkTypesToFinanceRates = (
  infoWorkTypes: InfoWorkType[],
): FinanceRate[] =>
  infoWorkTypes.reduce((final: FinanceRate[], current: InfoWorkType) => {
    const { rates } = current;
    return [...final, ...rates];
  }, []);

export const extractAllFinanceRatesFromInfoWorkTypes = (
  infoWorkTypes: InfoWorkType[],
): FinanceRate[] =>
  pipe(
    infoWorkTypes,
    reduceInfoWorkTypesToFinanceRates,
    extractUniqueFinanceRates,
  );

export const getRatesFromInfoWorkTypesByWorkType = (
  infoWorkTypes: InfoWorkType[],
  workType: WorkType,
): InfoWorkType | undefined =>
  infoWorkTypes.filter((type) => type.work_type === workType)?.[0];
